import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Sparkle, ArrowsClockwise, Ghost, CaretDown, CaretUp, Copy,
  EnvelopeSimple, Spinner, CurrencyDollar,
} from '@phosphor-icons/react';
import ComposeEmailDialog from './ComposeEmailDialog';

/* Crunchy autonomous agents — Pre-Call Briefs (contact card) and
   GhostBuster (pipeline cold-deal reactivator). */

export function PreCallBrief({ contactId }) {
  const [brief, setBrief] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!contactId) return;
    setBrief(null); setUpcoming(null); setOpen(false);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/agents/brief/${contactId}`, { params: { cached_only: true } });
        if (cancelled) return;
        setUpcoming(data.upcoming);
        if (data.brief) { setBrief(data.brief); setOpen(true); }
        else if (data.upcoming && data.upcoming.scheduled_date === new Date().toISOString().slice(0, 10)) {
          generate(false); // meeting today → auto-brief
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const generate = async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/agents/brief/${contactId}`, { params: { refresh } });
      setBrief(data.brief);
      setUpcoming(data.upcoming);
      setOpen(true);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  return (
    <div className="mt-1 bg-[#FFFBEB] border border-[#E5C158]" data-testid="precall-brief-panel">
      <div className="flex items-center justify-between px-2 py-1">
        <button onClick={() => setOpen(o => !o)} className="flex items-center gap-1.5 text-[11px] font-bold text-[#7A5C00]" data-testid="precall-brief-toggle">
          <Sparkle size={12} weight="fill" className="text-[#F39C12]" />
          Crunchy Pre-Call Brief
          {upcoming && (
            <span className="font-normal text-[#8A7020]">
              · next: {upcoming.activity_type} {upcoming.scheduled_date}{upcoming.scheduled_time ? ` ${upcoming.scheduled_time}` : ''}
            </span>
          )}
          {open ? <CaretUp size={10} /> : <CaretDown size={10} />}
        </button>
        <button
          onClick={() => generate(!!brief)}
          disabled={loading}
          className="flex items-center gap-1 text-[11px] font-bold text-[#0B5394] hover:underline disabled:opacity-50"
          data-testid="precall-brief-generate"
        >
          {loading ? <Spinner size={11} className="animate-spin" /> : <ArrowsClockwise size={11} weight="bold" />}
          {loading ? 'Briefing…' : brief ? 'Refresh' : 'Brief me'}
        </button>
      </div>
      {open && brief && (
        <div className="px-2 pb-2 grid sm:grid-cols-3 gap-2" data-testid="precall-brief-content">
          {[['WHO', brief.who, 'precall-brief-who'], ['HISTORY', brief.history, 'precall-brief-history'], ['OPENER', brief.opener, 'precall-brief-opener']].map(([label, text, tid]) => (
            text ? (
              <div key={label} className="bg-white border border-[#E5C158]/60 p-2" data-testid={tid}>
                <div className="text-[9px] font-black tracking-widest text-[#B08900] mb-0.5">{label}</div>
                <p className="text-[11px] text-[#333] leading-snug">{text}</p>
              </div>
            ) : null
          ))}
        </div>
      )}
    </div>
  );
}

export function GhostBusterPanel() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [draftingId, setDraftingId] = useState(null);
  const [draft, setDraft] = useState(null); // { draft:{subject,body}, contact, dealTitle }
  const [composeOpen, setComposeOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get('/agents/cold-deals', { params: { days: 14 } })
      .then(({ data }) => setData(data))
      .catch(() => {});
  }, []);

  if (!data || data.count === 0) return null;

  const draftEmail = async (deal) => {
    setDraftingId(deal.deal_id);
    try {
      const { data: d } = await api.post(`/agents/ghostbuster/${deal.deal_id}`);
      setDraft({ ...d, dealTitle: deal.title });
      setCopied(false);
    } catch { /* silent */ } finally { setDraftingId(null); }
  };

  const mergedBody = draft?.draft?.body?.replaceAll('{first_name}', draft?.contact?.first_name || 'there') || '';

  const copyDraft = () => {
    navigator.clipboard.writeText(`Subject: ${draft.draft.subject}\n\n${mergedBody}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mb-4 bg-amber-50 border border-amber-300 rounded-sm" data-testid="ghostbuster-panel">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
        data-testid="ghostbuster-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-bold text-amber-900">
          <Ghost size={16} weight="fill" className="text-amber-600" />
          GhostBuster: {data.count} deal{data.count > 1 ? 's have' : ' has'} gone cold (no touch in {data.days}+ days)
        </span>
        <span className="flex items-center gap-1 text-xs font-bold text-amber-700">
          <Sparkle size={11} weight="fill" /> Crunchy can draft the re-engagement {expanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-amber-200 divide-y divide-amber-100" data-testid="ghostbuster-list">
          {data.deals.map((d) => (
            <div key={d.deal_id} className="flex items-center justify-between px-3 py-2 gap-3" data-testid={`ghostbuster-deal-${d.deal_id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#111827] truncate">{d.title}</div>
                <div className="text-xs text-[#4B5563] flex items-center gap-2 flex-wrap">
                  {d.value > 0 && <span className="inline-flex items-center gap-0.5 font-bold text-[#002FA7]"><CurrencyDollar size={11} weight="bold" />{Number(d.value).toLocaleString()}</span>}
                  <span className="capitalize">{d.stage}</span>
                  {d.contact && <span>{d.contact.first_name} {d.contact.last_name}</span>}
                  <span className="text-amber-700 font-bold">{d.days_cold} days cold</span>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => draftEmail(d)}
                disabled={draftingId === d.deal_id}
                className="bg-[#F39C12] hover:bg-[#d9880d] text-black rounded-sm text-xs h-7 gap-1 shrink-0"
                data-testid={`ghostbuster-draft-${d.deal_id}`}
              >
                {draftingId === d.deal_id ? <Spinner size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />}
                {draftingId === d.deal_id ? 'Drafting…' : 'Draft re-engagement'}
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="sm:max-w-[560px] rounded-sm" data-testid="ghostbuster-draft-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <Ghost size={16} weight="fill" className="text-amber-600" /> Re-engagement draft — {draft?.dealTitle}
            </DialogTitle>
          </DialogHeader>
          {draft && (
            <div className="space-y-3">
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-sm p-3">
                <div className="text-xs font-bold text-[#4B5563] mb-1">Subject: <span className="text-[#111827]" data-testid="ghostbuster-draft-subject">{draft.draft.subject}</span></div>
                <p className="text-sm text-[#111827] whitespace-pre-wrap leading-relaxed" data-testid="ghostbuster-draft-body">{mergedBody}</p>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={copyDraft} className="rounded-sm text-xs gap-1" data-testid="ghostbuster-copy-btn">
                  <Copy size={13} /> {copied ? 'Copied ✓' : 'Copy'}
                </Button>
                {draft.contact?.email && (
                  <Button size="sm" onClick={() => setComposeOpen(true)} className="bg-[#002FA7] hover:bg-[#002080] text-white rounded-sm text-xs gap-1" data-testid="ghostbuster-send-btn">
                    <EnvelopeSimple size={13} weight="bold" /> Open in Email
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ComposeEmailDialog
        open={composeOpen}
        contact={draft?.contact}
        initialSubject={draft?.draft?.subject || ''}
        initialBody={mergedBody}
        onClose={() => setComposeOpen(false)}
        onSent={() => { setComposeOpen(false); setDraft(null); }}
      />
    </div>
  );
}
